import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "../../../context/AuthContext";
import api from "../../../services/api";

interface JobItem {
  id: string;
  taskId: string;
  studentId: string;
  studentName: string;
  taskTitle: string;
  priority: string;
  status: string;
  createdAt: string;
}

export default function MarketingJobPoolPreview({ onClaim }: { onClaim?: () => void }) {
  const navigate = useNavigate();
  const { data: session }: any = useSession();
  const [jobs, setJobs] = useState<JobItem[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);

  const fetchJobs = async () => {
    try {
      const res = await api.get(`/service-pool?t=${Date.now()}`);
      const data = Array.isArray(res.data) ? res.data : [];
      setJobs(data.filter((i: any) => i.type === "EXPERT_JOB" && i.status === "OPEN"));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchJobs();
    const interval = setInterval(fetchJobs, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeft(scrollLeft > 10);
      setShowRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const amount = 400;
      scrollRef.current.scrollBy({ left: direction === "left" ? -amount : amount, behavior: "smooth" });
    }
  };

  const handleClaim = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await api.patch(`/service-pool/${id}`, { 
        status: "ASSIGNED", 
        type: "EXPERT_JOB", 
        staffId: session?.user?.id 
      });
      fetchJobs();
      if (onClaim) onClaim();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="relative group">
      <div className="flex items-center justify-between px-2 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-500/10 text-orange-600 flex items-center justify-center shadow-sm">
            <span className="material-symbols-outlined text-xl fill-1">engineering</span>
          </div>
          <div>
            <h3 className="text-xl font-black text-on-surface tracking-tighter">Global Pickup Pool</h3>
            <p className="text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest text-orange-500/60">Available for Immediate Claim</p>
          </div>
        </div>
        <button 
          onClick={() => navigate('/marketing/pool')}
          className="text-[10px] font-black uppercase text-primary tracking-widest hover:underline cursor-pointer"
        >
          View Full Pool
        </button>
      </div>

      {/* Navigation Chevrons */}
      {showLeft && (
        <button 
          onClick={() => scroll("left")}
          className="absolute left-[-20px] top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-2xl border border-outline-variant/10 flex items-center justify-center hover:bg-slate-50 transition-all text-slate-400 group/btn cursor-pointer"
        >
          <span className="material-symbols-outlined group-hover/btn:text-primary">chevron_left</span>
        </button>
      )}
      {showRight && jobs.length > 2 && (
        <button 
          onClick={() => scroll("right")}
          className="absolute right-[-20px] top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-2xl border border-outline-variant/10 flex items-center justify-center hover:bg-slate-50 transition-all text-slate-400 group/btn cursor-pointer"
        >
          <span className="material-symbols-outlined group-hover/btn:text-primary">chevron_right</span>
        </button>
      )}

      <div 
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-6 overflow-x-auto no-scrollbar scroll-smooth p-6 -m-6 pb-12 cursor-grab active:cursor-grabbing"
      >
        {jobs.map((job) => (
          <div 
            key={job.id}
            onClick={() => navigate(`/tasks/${job.taskId}?userId=${job.studentId}`)}
            className="flex-shrink-0 w-[400px] bg-surface-container-low border border-t-8 border-orange-500 rounded-[2.5rem] p-8 shadow-sm hover:shadow-2xl hover:translate-y-[-8px] transition-all flex flex-col justify-between gap-6 cursor-pointer relative overflow-hidden group/card"
          >
            <div className="absolute top-0 right-0 p-6 opacity-5 group-hover/card:opacity-20 transition-opacity">
              <span className="material-symbols-outlined text-7xl fill-1">monitoring</span>
            </div>

            <div className="flex justify-between items-start relative">
              <div>
                <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Production Client</p>
                <h4 className="text-2xl font-black text-on-surface tracking-tighter leading-tight group-hover/card:text-orange-600 transition-colors">{job.studentName}</h4>
              </div>
              <span className={`text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter shadow-sm ${
                job.priority === "VIP" ? "bg-cyan-500 text-white" :
                job.priority === "PREMIUM" ? "bg-amber-500 text-white" :
                "bg-slate-500 text-white"
              }`}>
                {job.priority}
              </span>
            </div>

            <div className="space-y-4 relative">
              <div className="h-px w-full bg-outline-variant/10" />
              <div>
                <p className="text-[10px] font-black text-on-surface-variant/40 uppercase tracking-[0.2em] mb-1">Execution Goal</p>
                <p className="text-sm font-bold text-on-surface leading-tight line-clamp-2">{job.taskTitle}</p>
              </div>
            </div>

            <button 
              onClick={(e) => handleClaim(e, job.id)}
              className="w-full py-4 bg-orange-600 text-white rounded-[1.5rem] font-black text-[11px] uppercase tracking-widest shadow-xl shadow-orange-500/20 hover:bg-orange-700 hover:translate-y-[-2px] active:scale-[0.98] transition-all cursor-pointer"
            >
              Claim Execution Unit
            </button>
          </div>
        ))}

        {jobs.length === 0 && (
          <div className="w-full py-20 bg-surface-container-high/20 rounded-[3rem] border-2 border-dashed border-outline-variant/10 flex flex-col items-center justify-center gap-4 animate-in fade-in duration-700">
            <span className="material-symbols-outlined text-6xl text-slate-200 fill-1">auto_awesome</span>
            <p className="text-on-surface-variant font-medium italic opacity-60">Execution pool is clear. Excellent throughput!</p>
          </div>
        )}
      </div>
    </div>
  );
}
